import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { getCachedAuthUser } from '../../services/network';

/**
 * Onboarding draft (docs/v150-spec.md §3.3) — the answers of the first-run journey,
 * persisted as 'bt_onboarding' so they survive the auth hop: email sign-up leaves
 * through /register → /confirm and comes back through '/', the web Apple button is a
 * full-page redirect, and a parent may confirm their email in a later session.
 *
 * One draft per device. It records the Cognito sub once signed in; a draft that
 * belongs to a different sub is discarded (syncDraftOwner), so answers never leak to
 * the next account on a shared phone. The draft is cleared on completion, on "Skip for
 * now" and on sign-out (resetOnboarding).
 *
 * `step === null` means there is no draft. Every storage access is wrapped: in a
 * private window or with storage blocked the draft simply lives in memory.
 *
 * Setup progress (passHandled, answersSaved, babyId, sleepLogged) lives here too, so a
 * retried or resumed setup never redeems, posts or creates anything twice. `pendingBaby`
 * is the exact POST /babies body of an attempt that has not heard back yet: when that
 * request committed but its answer was lost (a timeout, the app killed mid-request), the
 * baby it made is recognised by it (isDraftOwnBaby / adoptPendingBaby) instead of being
 * taken for one the account already had.
 *
 * Separate from the draft: the per-user, per-device outcome of the journey
 * ('done' | 'skipped', key 'bt_onboarding_status:<sub>') that FirstRunGate reads, and the
 * journey record ('bt_onboarding_journey:<sub>', recordJourneyEnd) that outlives the draft.
 */

export const ONBOARDING_STORAGE_KEY = 'bt_onboarding';
const STORE_VERSION = 1;
const STATUS_KEY_PREFIX = 'bt_onboarding_status:';
export const JOURNEY_KEY_PREFIX = 'bt_onboarding_journey:';

function freshDraft() {
  return {
    step: null, // current step (onboardingSteps.js); null = no draft
    sub: null, // Cognito sub the draft belongs to; null until signed in
    startedAt: null, // epoch ms
    ageBand: null, // 'newborn' | 'infant' | 'toddler' | 'expecting'
    slide: 0, // feature slide index
    passCode: null, // "Have a pass code?" on the sign-up step (normalised)
    name: '',
    dateOfBirth: '', // YYYY-MM-DD
    dueDate: '', // YYYY-MM-DD (expecting)
    bornEarly: null, // 'yes' | 'no' | 'unsure'
    weeksEarly: null, // 4–20, with 'yes' only
    napsAnswer: null, // 0–5 in halves (5 = "5+")
    lastSleepAnswer: null, // 'time' | 'asleep' | 'unsure'
    fellAsleepAt: null, // ISO — answer 'time'
    wokeAt: null, // ISO — answer 'time'
    asleepSince: null, // ISO — answer 'asleep'
    notifications: null, // 'allowed' | 'skipped' | 'denied' | 'na'
    offersConsent: false,
    goals: [],
    goalsOther: '',
    sleepGoals: [],
    sleepGoalsOther: '',
    startedTracked: false,
    signupTracked: false, // the sign-up step was shown (its funnel event sent); see recordJourneyEnd
    setupStarted: false, // a setup run has begun — a later run checks for a baby it already made
    passHandled: false,
    passRedeemed: false,
    answersSaved: false,
    babyId: null,
    pendingBaby: null, // the POST /babies body sent, until its baby id is known
    sleepLogged: false,
  };
}

const DRAFT_KEYS = Object.keys(freshDraft());
// Fields that default to null hold either null or one type.
const NULLABLE_NUMBERS = ['startedAt', 'weeksEarly', 'napsAnswer'];
const NULLABLE_BABY_BODIES = ['pendingBaby'];

const isPlainObject = (value) => !!value && typeof value === 'object' && !Array.isArray(value);

// A POST /babies body as buildBabyPayload makes it: name and dateOfBirth strings, plus
// prematurity / sleepSettings objects when answered.
function isBabyBody(value) {
  return isPlainObject(value) && typeof value.name === 'string' && typeof value.dateOfBirth === 'string';
}

function cleanBabyBody(value) {
  const out = { name: value.name, dateOfBirth: value.dateOfBirth };
  ['prematurity', 'sleepSettings'].forEach((key) => {
    if (isPlainObject(value[key])) out[key] = value[key];
  });
  return out;
}

function draftOf(state) {
  const out = {};
  DRAFT_KEYS.forEach((key) => {
    out[key] = state[key];
  });
  return out;
}

function wellTyped(key, value, expected) {
  if (Array.isArray(expected)) return Array.isArray(value);
  if (expected !== null) return typeof value === typeof expected && (typeof value !== 'number' || Number.isFinite(value));
  if (value === null) return true;
  if (NULLABLE_BABY_BODIES.includes(key)) return isBabyBody(value);
  return NULLABLE_NUMBERS.includes(key) ? Number.isFinite(value) : typeof value === 'string';
}

// Keep only well-typed values from storage — a hand-edited or older shape must never
// crash a step that expects, say, an array.
function sanitise(persisted) {
  if (!persisted || typeof persisted !== 'object') return {};
  const base = freshDraft();
  const out = {};
  DRAFT_KEYS.forEach((key) => {
    if (!(key in persisted) || !wellTyped(key, persisted[key], base[key])) return;
    const value = persisted[key];
    if (Array.isArray(value)) out[key] = value.filter((item) => typeof item === 'string');
    else if (NULLABLE_BABY_BODIES.includes(key) && value) out[key] = cleanBabyBody(value);
    else out[key] = value;
  });
  return out;
}

const safeStorage = {
  getItem: (name) => {
    try {
      return window.localStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      window.localStorage.setItem(name, value);
    } catch {
      /* quota / private mode — the draft lives in memory for this session */
    }
  },
  removeItem: (name) => {
    try {
      window.localStorage.removeItem(name);
    } catch {
      /* nothing to remove */
    }
  },
};

export const useOnboardingStore = create(
  persist(() => freshDraft(), {
    name: ONBOARDING_STORAGE_KEY,
    version: STORE_VERSION,
    storage: createJSONStorage(() => safeStorage),
    partialize: draftOf,
    // No earlier version exists; anything unrecognised starts a clean draft.
    migrate: () => freshDraft(),
    merge: (persisted, current) => ({ ...current, ...sanitise(persisted) }),
  })
);

/** Merge answers / progress into the draft. */
export function updateDraft(patch) {
  useOnboardingStore.setState(patch);
}

/** Start a new draft at `step` (any previous draft is replaced). */
export function beginDraft({ step, sub = null }) {
  useOnboardingStore.setState({ ...freshDraft(), step, sub, startedAt: Date.now() });
}

/** Clear the draft (completion, "Skip for now", sign-out, account deletion). */
export function resetOnboarding() {
  useOnboardingStore.setState(freshDraft());
  try {
    useOnboardingStore.persist.clearStorage();
  } catch {
    /* storage unavailable — the in-memory reset is enough */
  }
}

function signedInSub() {
  return getCachedAuthUser()?.sub || null;
}

/** True when `state` holds a draft that `sub` (null = unknown) may continue. */
export function draftBelongsTo(state, sub) {
  if (!state || !state.step) return false;
  return !(state.sub && sub && state.sub !== sub);
}

/**
 * Synchronous: is a first-run draft in progress for whoever is signed in on this
 * device (or for nobody yet)? Reads the cached Cognito session, never the network.
 */
export function isOnboardingInProgress() {
  return draftBelongsTo(useOnboardingStore.getState(), signedInSub());
}

/**
 * Bind the draft to the signed-in account: a signed-out draft is claimed, a draft that
 * belongs to a different account is discarded.
 */
export function syncDraftOwner(sub) {
  if (!sub) return;
  const state = useOnboardingStore.getState();
  if (!state.step) return;
  if (state.sub && state.sub !== sub) {
    resetOnboarding();
    return;
  }
  if (!state.sub) useOnboardingStore.setState({ sub });
}

/** Is `baby` the one a POST /babies with body `sent` created? (Name trimmed, same date.) */
export function isPendingBabyMatch(baby, sent) {
  if (!baby || !isBabyBody(sent)) return false;
  const name = sent.name.trim();
  return !!name && String(baby.name || '').trim() === name && !!sent.dateOfBirth && baby.dateOfBirth === sent.dateOfBirth;
}

/**
 * Does `baby` belong to the setup in `draft` ({ babyId, setupStarted, pendingBaby })? By
 * id once known; before that, a setup that already sent POST /babies owns the baby that
 * matches what it sent — the request may have committed with its answer lost.
 */
export function isDraftOwnBaby(baby, draft) {
  if (!baby || !draft) return false;
  if (draft.babyId) return baby.babyId === draft.babyId;
  return !!draft.setupStarted && isPendingBabyMatch(baby, draft.pendingBaby);
}

/**
 * Adopt the baby an interrupted setup created (it matches `pendingBaby`) as the draft's
 * own, so the setup resumes with it instead of creating a second one. `pendingBaby` is
 * kept: prepareSetup still applies any answer edited since the request went out.
 * Returns the adopted id, or null when there is nothing to adopt.
 */
export function adoptPendingBaby(babies) {
  const state = useOnboardingStore.getState();
  if (!state.step || state.babyId || !state.setupStarted || !state.pendingBaby) return null;
  const list = Array.isArray(babies) ? babies : [];
  const match = list.find((baby) => isPendingBabyMatch(baby, state.pendingBaby));
  if (!match?.babyId) return null;
  updateDraft({ babyId: match.babyId });
  return match.babyId;
}

// Journey record — how this user's first-run journey ended on this device, kept after
// the draft is cleared: { at, ageBand, signup }.
//   ageBand  the age answer ('expecting' → BabySetupPage explains a baby is added once born)
//   signup   the journey included the sign-up step, whose links carry the current
//            Privacy Policy (so the dashboard's policy-update notice is not needed)
// Its presence also marks an account whose first run happened on this device (the
// Premium nap-reminder default in features/offer/trialOffer.js).
const journeyInMemory = new Map();

/** Record the end of the journey (setup finished, or "Skip for now") for `sub`. */
export function recordJourneyEnd(sub, draft = useOnboardingStore.getState()) {
  if (!sub) return;
  const record = { at: Date.now(), ageBand: draft?.ageBand || null, signup: !!draft?.signupTracked };
  try {
    window.localStorage.setItem(JOURNEY_KEY_PREFIX + sub, JSON.stringify(record));
    journeyInMemory.delete(sub);
  } catch {
    journeyInMemory.set(sub, record);
  }
}

/** { at, ageBand, signup } for `sub` on this device, or null. Never throws. */
export function readJourney(sub) {
  if (!sub) return null;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(JOURNEY_KEY_PREFIX + sub) || 'null');
    if (isPlainObject(parsed)) return parsed;
  } catch {
    /* fall back to memory */
  }
  return journeyInMemory.get(sub) || null;
}

// Fallback for a device that refuses storage writes, so a "Skip for now" still holds
// for the rest of the session instead of sending the user straight back to onboarding.
const statusInMemory = new Map();

/** 'done' | 'skipped' | null — this user's first-run outcome on this device. */
export function readOnboardingStatus(sub) {
  if (!sub) return null;
  try {
    const stored = window.localStorage.getItem(STATUS_KEY_PREFIX + sub);
    if (stored === 'done' || stored === 'skipped') return stored;
  } catch {
    /* fall back to memory */
  }
  return statusInMemory.get(sub) || null;
}

export function markOnboardingStatus(sub, status) {
  if (!sub || (status !== 'done' && status !== 'skipped')) return;
  try {
    window.localStorage.setItem(STATUS_KEY_PREFIX + sub, status);
    statusInMemory.delete(sub);
  } catch {
    statusInMemory.set(sub, status);
  }
}
